import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useApp } from '../../context/AppContext';
import './Admin.css';

function SeverityBar({ value, max = 5 }) {
  const pct = (value / max) * 100;
  const color = value >= 4 ? 'var(--red)' : value >= 2.5 ? 'var(--yellow-dark)' : 'var(--green)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
      <div style={{ flex: 1, height: '6px', background: 'var(--gray-card)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 'var(--radius-full)', transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 700, color }}>{value.toFixed(1)}</span>
    </div>
  );
}

export default function ParticipantDetailPage() {
  const { sessionId } = useParams();
  const { getSession, getAgent } = useApp();
  const session = getSession(sessionId);
  const agent = session ? getAgent(session.agentId) : null;
  const [activeTab, setActiveTab] = useState('summary');

  if (!session) return (
    <AdminLayout title="غير موجود">
      <div className="empty-state"><p>لم يتم العثور على هذه المقابلة</p></div>
    </AdminLayout>
  );

  const { analysis, messages } = session;

  const jsonPreview = analysis ? {
    session_id: session.id,
    agent: agent?.name,
    status: session.status,
    duration_minutes: session.durationMinutes,
    ...(analysis || {}),
  } : null;

  const TABS = [
    { id: 'summary', label: 'الملخص' },
    { id: 'transcript', label: 'نص المحادثة' },
    { id: 'json', label: 'JSON' },
  ];

  return (
    <AdminLayout
      title=""
      actions={
        <Link to="/admin/results" className="btn btn-ghost btn-sm">
          <ChevronRight size={14} />
          العودة
        </Link>
      }
    >
      {/* Header card */}
      <div className="card" style={{ marginBottom: '14px' }}>
        <div className="breadcrumb" style={{ marginBottom: '8px' }}>
          <Link to="/admin/results">النتائج</Link> / #{session.id}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ color: 'var(--brown)', marginBottom: '4px' }}>مقابلة #{session.id}</h2>
            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: 'var(--brown-muted)', flexWrap: 'wrap' }}>
              <span>الوكيل: {agent?.name || '—'}</span>
              <span>·</span>
              <span>{new Date(session.createdAt).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              {session.durationMinutes && <><span>·</span><span>{session.durationMinutes} دقيقة</span></>}
            </div>
          </div>
          <span className={`status ${session.status === 'completed' ? 'status-done' : 'status-progress'}`}>
            {session.status === 'completed' ? 'مكتمل' : 'جارٍ'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: '2px solid var(--yellow)', paddingBottom: '2px' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="btn btn-ghost btn-sm"
            style={activeTab === tab.id ? { background: 'var(--yellow)', color: 'var(--brown)', borderColor: 'var(--yellow)' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary tab */}
      {activeTab === 'summary' && (
        <div className="detail-grid">
          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analysis?.summary && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>ملخص AI</h3>
                <p style={{ fontSize: '14px', color: 'var(--brown-mid)', lineHeight: 1.75 }}>{analysis.summary}</p>
              </div>
            )}

            {analysis?.painPoints?.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>نقاط الألم</h3>
                {analysis.painPoints.map((p, i) => (
                  <div key={i} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className={`severity-dot ${p.severity >= 4 ? 'sev-high' : p.severity >= 2.5 ? 'sev-mid' : 'sev-low'}`} />
                      <span className="tag tag-pain">{p.label}</span>
                    </div>
                    <SeverityBar value={p.severity} />
                  </div>
                ))}
              </div>
            )}

            {analysis?.keyQuotes?.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>اقتباسات مهمة</h3>
                {analysis.keyQuotes.map((q, i) => (
                  <div key={i} className="quote-card">
                    "{q.text}"
                    <div className="quote-attr">سؤال {q.questionIndex}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analysis?.userNeeds?.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>احتياجات المستخدم</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {analysis.userNeeds.map((n, i) => <span key={i} className="tag tag-need">{n}</span>)}
                </div>
              </div>
            )}

            {analysis?.opportunities?.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>فرص التحسين</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {analysis.opportunities.map((o, i) => <span key={i} className="tag tag-opp">{o}</span>)}
                </div>
              </div>
            )}

            {analysis?.tags?.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>الكلمات المفتاحية</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {analysis.tags.map((t, i) => <span key={i} className="tag tag-neutral">{t}</span>)}
                </div>
              </div>
            )}

            {typeof analysis?.severityScore === 'number' && (
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>درجة الخطورة الإجمالية</h3>
                <SeverityBar value={analysis.severityScore} />
                <p style={{ fontSize: '11px', color: 'var(--brown-muted)', marginTop: '6px' }}>
                  {analysis.severityScore >= 4 ? 'عالية – تحتاج أولوية فورية' :
                   analysis.severityScore >= 2.5 ? 'متوسطة – تحتاج متابعة' : 'منخفضة – يمكن تأجيلها'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transcript tab */}
      {activeTab === 'transcript' && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>نص المحادثة الكامل ({messages.length} رسالة)</h3>
          {messages.length === 0 ? (
            <div className="empty-state"><p>لا توجد رسائل محفوظة</p></div>
          ) : (
            <div className="transcript-wrap">
              {messages.map((m, i) => (
                <div key={i}>
                  <div className="t-label">{m.role === 'assistant' ? 'الوكيل' : 'المشارك'}</div>
                  <div className={`t-bubble ${m.role === 'assistant' ? 't-ai' : 't-user'}`}>{m.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* JSON tab */}
      {activeTab === 'json' && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>البيانات المنظمة (JSON)</h3>
          {jsonPreview ? (
            <div className="json-block">
              {JSON.stringify(jsonPreview, null, 2).split('\n').map((line, i) => {
                const keyMatch = line.match(/^(\s*)"([^"]+)":/);
                const strVal = line.match(/:\s*"(.+)"[,]?$/);
                const numVal = line.match(/:\s*([0-9.]+)[,]?$/);
                return (
                  <div key={i}>
                    {keyMatch ? (
                      <>
                        {line.slice(0, keyMatch.index + keyMatch[1].length)}
                        <span className="json-key">"{keyMatch[2]}"</span>
                        {':'}
                        {strVal ? <span className="json-str"> "{strVal[1]}"</span> :
                         numVal ? <span className="json-num"> {numVal[1]}</span> :
                         line.slice(keyMatch.index + keyMatch[0].length)}
                      </>
                    ) : line}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state"><p>لا توجد بيانات منظمة بعد — تكتمل بعد انتهاء المقابلة</p></div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
